'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormProps {
  onSwitchToSignUp: () => void;
}

function readEmailFromDom(): string {
  const el = document.getElementById('login-email') as HTMLInputElement | null;
  return (el?.value || '').trim().toLowerCase();
}

function formatAuthError(err: any): string {
  const code = String(err?.code || err?.name || '').toLowerCase();
  const msg = String(err?.message || '').trim();
  const status = err?.status;

  if (
    code.includes('user_not_found') ||
    /no account found|user not found/i.test(msg)
  ) {
    return 'No account found with this email. Please check your email address or click "Create your account" below.';
  }

  if (
    code.includes('invalid_credentials') ||
    /invalid login credentials|invalid email or password/i.test(msg)
  ) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  if (
    status === 429 ||
    code.includes('rate_limit') ||
    code.includes('over_email_send_rate_limit') ||
    /rate limit|too many|only request this once/i.test(msg)
  ) {
    return msg || 'Too many attempts. Please wait a minute and try again.';
  }

  if (/smtp|confirmation email|error sending/i.test(msg)) {
    return 'Email delivery is temporarily unavailable. Please try again later or contact support.';
  }

  if (
    /failed to fetch|networkerror|fetch error|network request failed/i.test(msg) ||
    /typeerror/i.test(code)
  ) {
    return 'Connection issue. Please check your connection and try again.';
  }

  return msg || 'Sign in failed. Please check your credentials and try again.';
}

export default function LoginForm({ onSwitchToSignUp }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      let profile = (result as any)?.profile;
      if (!profile && result?.user?.id) {
        try {
          const supabase = createClient();
          const { data: dbProfile } = await supabase
            .from('user_profiles')
            .select('user_type, onboarding_completed')
            .eq('id', result.user.id)
            .maybeSingle();
          if (dbProfile) profile = dbProfile;
        } catch {
          // Non-blocking
        }
      }

      const userType =
        profile?.user_type ||
        result?.user?.user_metadata?.user_type ||
        result?.user?.user_metadata?.role ||
        'professional';

      toast.success('Welcome back to AvenLink');
      if (userType === 'client') {
        router.push('/client-portal');
      } else if (profile?.onboarding_completed !== false) {
        router.push('/professional-dashboard');
      } else {
        router.push('/onboarding-screen');
      }
    } catch (err: any) {
      setIsLoading(false);
      const msg = formatAuthError(err) || 'Invalid email or password.';
      setError('email', { message: msg });
      toast.error(msg);
    }
  };

  const handleForgotPassword = async () => {
    // Browser autofill often fills the DOM without updating react-hook-form state
    const email = (getValues('email') || '').trim().toLowerCase() || readEmailFromDom();

    if (email) {
      setValue('email', email, { shouldValidate: false, shouldDirty: true });
    }

    if (!email) {
      setError('email', { message: 'Enter your email address first' });
      toast.error('Enter your email address first');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('email', { message: 'Enter a valid email' });
      toast.error('Enter a valid email');
      return;
    }

    clearErrors('email');
    setIsForgotLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://avenbeautylink.com';
      // redirectTo must be an allowed URL in Supabase Auth → URL Configuration → Redirect URLs
      // Add: https://avenbeautylink.com/auth/callback?type=recovery
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      toast.success('Password reset email sent!', {
        description: 'Check your inbox and spam folder. The link expires in 1 hour.',
      });
    } catch (err: any) {
      const message = formatAuthError(err);
      setError('email', { message });
      toast.error(message);
    } finally {
      setIsForgotLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-muted-foreground text-sm">Sign in to your AvenLink account</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-email">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="input-base w-full px-4 py-3 text-sm"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-foreground" htmlFor="login-password">
              Password
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isForgotLoading}
              className="text-xs font-medium hover:text-foreground transition-colors flex items-center gap-1"
              style={{ color: 'var(--primary)' }}
            >
              {isForgotLoading && <Loader2 size={10} className="animate-spin" />}
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className="input-base w-full px-4 py-3 text-sm pr-11"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-danger text-xs mt-1.5 font-medium">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-sm font-semibold flex items-center justify-center gap-2"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in to AvenLink'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        New to AvenLink?{' '}
        <button
          onClick={onSwitchToSignUp}
          className="font-semibold hover:text-foreground transition-colors"
          style={{ color: 'var(--primary)' }}
        >
          Create your account
        </button>
      </p>
    </div>
  );
}
